import { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import "./PositionSummary.styles.scss";

// Type Imports
import { IAlertMessage } from "@pulse-ui/types/components/AlertMessage";
import { ContextStateType, IOrgMetadata } from "../../context/context.types";
import { ISelectOption } from "@pulse-ui/types/components/DropdownInput";
import { INormalizedPosition } from "@pulse-ui/codegen/generated_ts/portfolio";
import { IAssetDefinition } from "@pulse-ui/codegen/generated_ts/refdata";
import { IAGPosSummaryDataRow } from "@pulse-ui/types/components/AGTable";
import { ColDef, SideBarDef } from "ag-grid-community";
import { ModifiedNormalizedPosition } from "./PositionSummary.types";

// Component Imports
import { AlertMessage, AGTable } from "../../components";
import { AutocompleteInput } from "../../components/Inputs";
import { Loading } from "../../layout";
import ViewsToolPanel from "../../components/AGToolPanels/ViewsToolPanel/ViewsToolPanel";
import AccountStatusCell from "@/components/AGCellRenderers/AccountStatusCell";

// Material UI Imports
import { SvgIcon } from "@mui/material";
import dayjs from "dayjs";
import utcPlugin from "dayjs/plugin/utc";

// Package Imports
import { v4 as uuidv4 } from "uuid";

// Context
import { useAppContext } from "../../context/context";

// SVG Imports
import LogoSVG from "@/svgs/pulse_prime_logo.svg?react";

// Promise/Helper Function Imports
import {
  getOrgMetadata,
  getOrgs,
  getPricingAssets,
  handleGetPositionSummaryData,
  setOrgMetadata,
  setQuoteAssetInContext,
} from "../../utils/promises";
import {
  currencyFormatter,
  customNumAggFunc,
  customNumericSortFunc,
  customStatusAggFunc,
  customTextAggFunc,
  numericFormatterNoPrecision,
} from "../../utils/helper_functions";
import { useAlert } from "@/utils/hooks/useAlert/useAlert";

const PositionSummary = () => {
  // State - START -------------------------------------
  // Query Params States
  const USDT = "USDT";
  const [queryParams, setQueryParams] = useSearchParams({
    quote_asset: "",
  });
  const location_origin = window.location.origin;
  const quote_asset_param = queryParams.get("quote_asset");
  const gridRef = useRef();
  const [rowData, setRowData] = useState<IAGPosSummaryDataRow[]>([]);
  const [initialRender, setInitialRender] = useState<boolean>(true);
  const [displayTable, setDisplayTable] = useState<boolean>(false);
  dayjs.extend(utcPlugin);
  const [loading, setLoading] = useState<boolean>(true);
  const [refdataAssets, setRefdataAssets] = useState<ISelectOption[]>([]);

  // Error Handling / Alert Message States
  const { alerts, pushAlert, closeAlert, clearAlerts } = useAlert();

  // Context States
  const { context_state, update_org_metadata, set_quote_asset } =
    useAppContext();

  const stringified_local_metadata: string | null =
    localStorage.getItem("pulse-ctx");
  const local_metadata: ContextStateType | null = stringified_local_metadata
    ? JSON.parse(stringified_local_metadata)
    : null;
  // State - END ---------------------------------------

  // Methods - START -----------------------------------
  const handleNavigate = (quote_asset: string | null) => {
    setRowData([]);
    setQueryParams((prev: URLSearchParams) => {
      prev.set("quote_asset", quote_asset || "");

      return prev;
    });
  };

  const handleAutocompleteDropdownChange = (
    _: React.SyntheticEvent,
    newValue: ISelectOption | null
  ) => {
    if (alerts.length > 0) clearAlerts();

    if (newValue) {
      set_quote_asset(newValue.value);
      handleNavigate(newValue.value);
    } else {
      set_quote_asset(null);
      handleNavigate(null);
    }
  };

  const generateDisplayNames = (org_metadata: IOrgMetadata) => {
    const temp_map: Map<string, string> = new Map();

    for (let portfolio of org_metadata.portfolios) {
      temp_map.set(portfolio.portfolio_id, portfolio.display_name);

      if (portfolio.accounts) {
        for (let account of portfolio.accounts) {
          temp_map.set(account.account_id, account.display_name);
        }
      }
    }

    return temp_map;
  };

  const getViewValidations = (
    quote_asset: string | null,
    local_metadata: any
  ) => {
    const view_validations = {
      val_quote_asset: !!quote_asset,
      local_metadata_quote_asset: !!local_metadata?.selected_quote_asset,
    };

    return view_validations;
  };

  const handleInitialDataLoad = async () => {
    setLoading(true);

    try {
      const get_orgs_res = await getOrgs(location_origin);
      const get_org_metadata_res = await getOrgMetadata(
        get_orgs_res.data.org_id,
        location_origin
      );
      await setOrgMetadata(get_org_metadata_res.data, update_org_metadata);

      const get_pricing_assets_res = await getPricingAssets(location_origin);

      const assets_map = get_pricing_assets_res.data.map(
        (asset: IAssetDefinition) => {
          return {
            label: asset,
            value: asset,
          };
        }
      );

      setRefdataAssets(assets_map);

      if (context_state.org_metadata.portfolios) {
        const { val_quote_asset, local_metadata_quote_asset } =
          getViewValidations(quote_asset_param, local_metadata);

        let quote_asset_res: string | null = null;

        if (!val_quote_asset && !local_metadata_quote_asset) {
          quote_asset_res = USDT;

          handleNavigate(quote_asset_res);
        } else if (val_quote_asset && !local_metadata_quote_asset) {
          quote_asset_res = quote_asset_param;

          handleNavigate(quote_asset_res);
        } else if (val_quote_asset && local_metadata_quote_asset) {
          quote_asset_res = quote_asset_param;

          handleNavigate(quote_asset_res);
        } else if (!val_quote_asset && local_metadata_quote_asset) {
          quote_asset_res = context_state.selected_quote_asset;

          handleNavigate(quote_asset_res);
        } else {
          quote_asset_res = USDT;

          handleNavigate(quote_asset_res);
        }

        await setQuoteAssetInContext(quote_asset_res, set_quote_asset);

        let handle_get_position_summary_data_res_arr: ModifiedNormalizedPosition[] =
          [];

        let promises = context_state.org_metadata.portfolios.map(
          (portfolio) => {
            if (portfolio.accounts) {
              if (portfolio.accounts.length > 0) {
                return handleGetPositionSummaryData(
                  portfolio,
                  quote_asset_res || USDT,
                  location_origin
                )
                  .then((handle_get_position_summary_data_res) => {
                    if (handle_get_position_summary_data_res.status === 204) {
                      pushAlert({
                        id: uuidv4(),
                        message: "No Position Summary rows found.",
                        status: 204,
                      });
                    }

                    return handle_get_position_summary_data_res.data;
                  })
                  .catch((handle_get_position_summary_data_error) => {
                    pushAlert({
                      id: uuidv4(),
                      message: handle_get_position_summary_data_error.error,
                      status:
                        handle_get_position_summary_data_error?.status || 500,
                    });
                    return null;
                  });
              } else {
                return Promise.resolve(null);
              }
            }
          }
        );

        const promises_res = await Promise.all(promises);
        handle_get_position_summary_data_res_arr = promises_res.filter(
          (res) => res !== null
        );

        parseAndSetTableData(handle_get_position_summary_data_res_arr);
      }
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message: error_res.message,
        status: error_res?.status || 500,
      });
    } finally {
      setInitialRender(false);
      setLoading(false);
    }
  };

  const parseAndSetTableData = (table_data: ModifiedNormalizedPosition[]) => {
    const display_names = generateDisplayNames(context_state.org_metadata);

    let temp_row_data_arr: IAGPosSummaryDataRow[] = [];

    for (let item of table_data) {
      for (let key in item) {
        if (key === "portfolio_id") continue;

        let position = item[key] as INormalizedPosition;

        let row: IAGPosSummaryDataRow = {
          row_id: uuidv4(),
          net_exposure: position.net_exposure?.toLocaleString() || "-",
          account: position?.account || "-",
          account_display_name: display_names.get(position.account) || "-",
          account_wallet: position.account_wallet || "-",
          balance_qty: position.balance_qty?.toLocaleString() || "-",
          balance_notional: position.balance_notional?.toLocaleString() || "-",
          gmv_notional: position.gmv_notional?.toLocaleString() || "-",
          long_qty: position.long_qty?.toLocaleString() || "-",
          long_notional: position.long_notional?.toLocaleString() || "-",
          portfolio: position?.portfolio || "-",
          portfolio_display_name: display_names.get(position.portfolio) || "-",
          short_qty: position.short_qty?.toLocaleString() || "-",
          short_notional: position.short_notional?.toLocaleString() || "-",
          symbol: position?.symbol || "-",
          underlier: position?.underlier || "-",
          venue: position?.venue || "",
          volume: position.volume?.toLocaleString() || "-",
          strategy: position?.strategy || "-",
          account_status: position.account_status?.toString() || null,
          account_status_message: position.account_status_message,
        };

        temp_row_data_arr.push(row);
      }
    }

    setRowData(temp_row_data_arr);
  };

  // Column Definitions: Defines & controls grid columns.
  const [colDefs] = useState<ColDef<IAGPosSummaryDataRow>[]>([
    {
      headerName: "Status",
      field: "account_status",
      cellClass: "ag-center-aligned-cell",
      cellRenderer: AccountStatusCell,
      cellRendererParams: (params: any) => {
        if (!params) {
          return {
            status: null,
            status_message: null,
          };
        }

        // For group rows, use the aggregated value
        if (params.node?.group) {
          return {
            status: params.value || null,
            status_message: null,
          };
        }

        // For detail rows, use the data from the row
        if (!params.data) {
          return {
            status: null,
            status_message: null,
          };
        }

        return {
          status: params.data.account_status || null,
          status_message: params.data.account_status_message || null,
        };
      },
      aggFunc: customStatusAggFunc,
    },
    {
      headerName: "Portfolio Name",
      field: "portfolio_display_name",
      aggFunc: customTextAggFunc,
      cellClass: "ag-center-aligned-cell",
    },
    {
      headerName: "Portfolio ID",
      field: "portfolio",
      cellClass: "ag-center-aligned-cell",
      aggFunc: customTextAggFunc,
      hide: true,
    },
    {
      headerName: "Account Name",
      field: "account_display_name",
      aggFunc: customTextAggFunc,
      cellClass: "ag-center-aligned-cell",
    },
    {
      headerName: "Account ID",
      field: "account",
      cellClass: "ag-center-aligned-cell",
      aggFunc: customTextAggFunc,
      hide: true,
    },
    {
      headerName: "Symbol",
      field: "symbol",
      cellClass: "ag-center-aligned-cell",
      aggFunc: customTextAggFunc,
    },
    {
      headerName: "$Gmv",
      field: "gmv_notional",
      aggFunc: customNumAggFunc,
      valueFormatter: (params: any) => currencyFormatter(params, "$"),
      comparator: customNumericSortFunc,
      cellClass: "ag-right-aligned-cell",
    },
    {
      headerName: "$Net Exposure",
      field: "net_exposure",
      aggFunc: customNumAggFunc,
      valueFormatter: (params: any) => currencyFormatter(params, "$"),
      cellClass: "ag-right-aligned-cell",
      comparator: customNumericSortFunc,
    },
    {
      headerName: "Balance Qty",
      field: "balance_qty",
      aggFunc: customNumAggFunc,
      valueFormatter: numericFormatterNoPrecision,
      comparator: customNumericSortFunc,
      cellClass: "ag-right-aligned-cell",
      hide: true,
    },
    {
      headerName: "$Balance",
      field: "balance_notional",
      aggFunc: customNumAggFunc,
      valueFormatter: (params: any) => currencyFormatter(params, "$"),
      comparator: customNumericSortFunc,
      cellClass: "ag-right-aligned-cell",
    },
    {
      headerName: "Long Qty",
      field: "long_qty",
      aggFunc: customNumAggFunc,
      valueFormatter: numericFormatterNoPrecision,
      comparator: customNumericSortFunc,
      cellClass: "ag-right-aligned-cell",
      hide: true,
    },
    {
      headerName: "$Long",
      field: "long_notional",
      aggFunc: customNumAggFunc,
      valueFormatter: (params: any) => currencyFormatter(params, "$"),
      comparator: customNumericSortFunc,
      cellClass: "ag-right-aligned-cell",
    },
    {
      headerName: "Short Qty",
      field: "short_qty",
      aggFunc: customNumAggFunc,
      valueFormatter: numericFormatterNoPrecision,
      comparator: customNumericSortFunc,
      cellClass: "ag-right-aligned-cell",
      hide: true,
    },
    {
      headerName: "$Short",
      field: "short_notional",
      aggFunc: customNumAggFunc,
      valueFormatter: (params: any) => currencyFormatter(params, "$"),
      comparator: customNumericSortFunc,
      cellClass: "ag-right-aligned-cell",
    },
    {
      headerName: "$Volume",
      field: "volume",
      aggFunc: customNumAggFunc,
      valueFormatter: (params: any) => currencyFormatter(params, "$"),
      comparator: customNumericSortFunc,
      cellClass: "ag-right-aligned-cell",
    },
    {
      headerName: "Underlier",
      field: "underlier",
      cellClass: "ag-center-aligned-cell",
      aggFunc: customTextAggFunc,
    },
    {
      headerName: "Venue",
      field: "venue",
      cellClass: "ag-center-aligned-cell",
      aggFunc: customTextAggFunc,
    },
    {
      headerName: "Account Wallet",
      field: "account_wallet",
      cellClass: "ag-center-aligned-cell",
      aggFunc: customTextAggFunc,
    },
    {
      headerName: "Strategy",
      field: "strategy",
      cellClass: "ag-center-aligned-cell",
      aggFunc: customTextAggFunc,
    },
  ]);

  const defaultColDef: ColDef = {
    filter: true,
    enableRowGroup: true,
  };

  const autoGroupColumnDef = useMemo(() => {
    return {
      headerName: "Group",
      minWidth: 280,
      filter: "agTextColumnFilter",
      cellRendererParams: {
        suppressCount: true,
      },
    };
  }, []);

  const sideBar = useMemo<
    SideBarDef | string | string[] | boolean | null
  >(() => {
    return {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
        },
        {
          id: "viewsToolPanel",
          labelDefault: "Views",
          labelKey: "viewsToolPanel",
          iconKey: "viewsIcon",
          toolPanel: ViewsToolPanel,
          toolPanelParams: {
            title: "Views",
            page: "position_summary",
            setLoading: setLoading,
            pushAlert: pushAlert,
          },
        },
      ],
      defaultToolPanel: "columns",
    };
  }, []);
  // Methods - END -------------------------------------

  // useEffects - START --------------------------------
  useEffect(() => {
    handleInitialDataLoad();
  }, []);

  useEffect(() => {
    if (!initialRender) {
      setLoading(true);

      let handle_get_position_summary_data_res_arr: ModifiedNormalizedPosition[] =
        [];

      let promises = context_state.org_metadata.portfolios.map((portfolio) => {
        if (portfolio.accounts) {
          if (portfolio.accounts.length > 0 && quote_asset_param) {
            return handleGetPositionSummaryData(
              portfolio,
              quote_asset_param,
              location_origin
            )
              .then((handle_get_position_summary_data_res) => {
                if (handle_get_position_summary_data_res.status === 204) {
                  pushAlert({
                    id: uuidv4(),
                    message: "No Positions Summary rows found.",
                    status: 204,
                  });
                }
                return handle_get_position_summary_data_res.data;
              })
              .catch((handle_get_position_summary_data_error) => {
                pushAlert({
                  id: uuidv4(),
                  message: handle_get_position_summary_data_error.error,
                  status: handle_get_position_summary_data_error?.status || 500,
                });
                return null;
              });
          } else {
            return Promise.resolve(null);
          }
        }
      });

      Promise.all(promises).then((results) => {
        handle_get_position_summary_data_res_arr = results.filter(
          (res) => res !== null
        );

        parseAndSetTableData(handle_get_position_summary_data_res_arr);
        setLoading(false);
      });
    }
  }, [quote_asset_param]);

  useEffect(() => {
    if (rowData.length > 0) {
      setDisplayTable(true);
    } else {
      setDisplayTable(false);
    }
  }, [rowData]);
  // useEffects - END ----------------------------------

  return (
    <div className="pulse-sidenav-adjusted-container pulse-bg-color--background-900">
      <SvgIcon
        sx={{
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
        }}
        children={<LogoSVG className="pulse-color--background-800" />}
      />

      {alerts.map((alert: IAlertMessage) => {
        return (
          <div className="pulse-mb-1" style={{ width: "100%" }} key={uuidv4()}>
            <AlertMessage
              id={alert.id}
              message={alert.message}
              status={alert.status}
              closeAlertMessage={closeAlert}
            />
          </div>
        );
      })}

      <div className="pulse-row position-summary-header-row position-summary-position-relative">
        <p className="pulse-typography-headline-6 pulse-color--background-50 pulse-col-md-2 pulse-col-lg-2 pulse-col-xl-2">
          Position Summary
        </p>
        <div className="position-summary-dropdown-inputs-container pulse-col-md-2 pulse-col-lg-2 pulse-col-xl-2 pulse-mb-2">
          <AutocompleteInput
            value={{
              label: quote_asset_param || "",
              value: quote_asset_param || "",
            }}
            inputLabel="Quote Asset"
            onChange={handleAutocompleteDropdownChange}
            disabled={refdataAssets.length < 1}
            options={refdataAssets}
          />
        </div>
      </div>

      {displayTable && (
        <AGTable
          gridRef={gridRef}
          rowData={rowData}
          colDefs={colDefs}
          defaultColDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          groupDisplayType="singleColumn"
          sideBar={sideBar}
          animateRows
          grandTotalRows="top"
        />
      )}

      {loading && <Loading />}
    </div>
  );
};

export default PositionSummary;
